import React from "react";
import { X, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { cn } from "../../lib/utils";
import { ModalType } from "../../types/modal";

export interface StatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: ModalType;
  title: string;
  message: string;
  header?: string;
  actionButton?: {
    text: string;
    onClick: () => void;
  };
  pngIcon?: {
    src: string; // string since it's base64
    alt?: string;
    size?: number;
  };
}

export const StatusModal: React.FC<StatusModalProps> = ({
  isOpen,
  onClose,
  type,
  title,
  message,
  header,
  actionButton,
  pngIcon,
}) => {
  if (!isOpen) return null;

  const config = {
    success: {
      icon: CheckCircle,
      iconColor: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
    },
    error: {
      icon: XCircle,
      iconColor: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
    },
    warning: {
      icon: AlertCircle,
      iconColor: "text-yellow-600",
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-200",
    },
    default: {
      icon: CheckCircle,
      iconColor: "text-gray-600",
      bgColor: "bg-gray-50",
      borderColor: "border-gray-200",
    },
  };

  const { icon: Icon, iconColor, bgColor, borderColor } = config[type];
  const iconSize = pngIcon?.size || 64;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-50 w-full max-w-md mx-4">
        <div
          className={cn(
            "bg-white rounded-[20px] shadow-lg border overflow-hidden h-80 w-[512px]",
            borderColor
          )}
        >
          {/* Header */}
          <div className={cn("px-6 py-4", header ? bgColor : "h-16")}>
            <div className="relative flex items-center justify-start w-full">
              {header && (
                <div className="flex items-center gap-3">
                  <Icon className={cn("h-6 w-6", iconColor)} />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {title}
                  </h3>
                </div>
              )}

              <button
                onClick={onClose}
                className="rounded-lg p-1 text-gray-500 hover:bg-white hover:text-gray-600 transition-colors absolute top-[2px] right-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* PNG Icon - Base64 image */}
            {pngIcon?.src && (
              <div className="flex justify-center mb-6">
                <img
                  src={pngIcon.src}
                  alt={pngIcon.alt || `${type} icon`}
                  width={iconSize}
                  height={iconSize}
                  className="object-contain"
                />
              </div>
            )}

            <p className="text-gray-900 text-center text-2xl">{message}</p>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              {actionButton && (
                <>
                  <button
                    onClick={onClose}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={actionButton.onClick}
                    className={cn(
                      "flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors",
                      type === "success" && "bg-green-600 hover:bg-green-700",
                      type === "error" && "bg-red-600 hover:bg-red-700",
                      type === "warning" && "bg-yellow-600 hover:bg-yellow-700"
                    )}
                  >
                    {actionButton.text}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
